import React, { useState, useRef } from 'react';
import { Mail, Send, CheckCircle, MapPin, AlertTriangle, Upload, X, FileText, Image, Phone, Zap, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    message: '',
    isEmergency: false
  });
  const [projectFiles, setProjectFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  
  const projectFileRef = useRef(null);
  const imageFileRef = useRef(null);

  const budgetRanges = [
    { value: 'under-500', label: 'Under $500' },
    { value: '500-1000', label: '$500 - $1,000' },
    { value: '1000-2500', label: '$1,000 - $2,500' },
    { value: '2500-5000', label: '$2,500 - $5,000' },
    { value: '5000-10000', label: '$5,000 - $10,000' },
    { value: 'over-10000', label: 'Over $10,000' },
    { value: 'not-sure', label: 'Not Sure - Need Quote' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBudgetChange = (value) => {
    setFormData(prev => ({ ...prev, budget: value }));
  };

  const handleProjectFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: (file.size / 1024).toFixed(1) + ' KB',
      type: file.type
    }));
    setProjectFiles(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
      size: (file.size / 1024).toFixed(1) + ' KB'
    }));
    setImageFiles(prev => [...prev, ...newImages].slice(0, 10));
  };

  const removeProjectFile = (index) => {
    setProjectFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(imageFiles[index].preview);
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Create FormData for multipart file upload
      const formDataPayload = new FormData();
      formDataPayload.append('name', formData.name);
      formDataPayload.append('email', formData.email);
      formDataPayload.append('phone', formData.phone || '');
      formDataPayload.append('budget', formData.budget || '');
      formDataPayload.append('message', formData.message);
      formDataPayload.append('isEmergency', formData.isEmergency);
      
      // Append all project files
      projectFiles.forEach((fileObj) => {
        formDataPayload.append('files', fileObj.file);
      });
      
      // Append all image files
      imageFiles.forEach((fileObj) => {
        formDataPayload.append('files', fileObj.file);
      });
      
      const response = await axios.post(`${API}/contact`, formDataPayload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setSubmitted(true);
        setTimeout(() => {
          setFormData({ name: '', email: '', phone: '', budget: '', message: '', isEmergency: false });
          setProjectFiles([]);
          setImageFiles([]);
          setSubmitted(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to send your message. Please try again or email us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmergencyClick = () => {
    setShowEmergencyModal(true);
  };

  const handleEmergencySubmit = () => {
    setFormData(prev => ({ ...prev, isEmergency: true }));
    setShowEmergencyModal(false);
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${companyInfo.email}`;
  };

  return (
    <section id="contact" className="py-16 sm:py-24 relative overflow-hidden" style={{ backgroundColor: '#3a2a23' }}>
      {/* Background Glow */}
      <div className="absolute top-0 left-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full blur-[120px] sm:blur-[150px] opacity-20" style={{ backgroundColor: '#d4c19c' }} />
      
      {/* Decorative Hexagon */}
      <div className="absolute bottom-0 right-0 opacity-5">
        <div 
          className="w-48 h-48 sm:w-72 sm:h-72"
          style={{
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            backgroundColor: '#d4c19c'
          }}
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Emergency Call Out Button */}
        <div className="mb-8 sm:mb-12">
          <button
            onClick={handleEmergencyClick}
            className="w-full group relative overflow-hidden rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 transition-all duration-300 hover:scale-[1.01]"
            style={{ 
              backgroundColor: 'rgba(220, 38, 38, 0.15)', 
              borderColor: '#dc2626'
            }}
          >
            <div className="relative flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-red-600 rounded-lg sm:rounded-xl flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-lg sm:text-2xl font-bold text-red-500">Emergency Electrical Issue?</h3>
                  <p className="text-sm sm:text-base text-red-400/80">Click here for priority service</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                <span className="text-red-500 font-semibold text-sm sm:text-base">Priority Response</span>
              </div>
            </div>
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          {/* Left - Contact Info */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4 sm:mb-6" style={{ backgroundColor: 'rgba(212, 193, 156, 0.15)', color: '#d4c19c' }}>
              <Zap className="w-4 h-4" />
              Get In Touch
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6" style={{ color: '#d4c19c' }}>
              Ready to Start Your Project?
            </h2>
            <p className="text-base sm:text-xl mb-6 sm:mb-10 leading-relaxed" style={{ color: '#c4b8a8' }}>
              Tell us about your electrical project. Upload photos and documents to help us provide an accurate quote.
            </p>

            {/* Email Card */}
            <div 
              onClick={handleEmailClick}
              className="group cursor-pointer rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 transition-all duration-300 hover:scale-[1.02]"
              style={{ backgroundColor: 'rgba(58, 42, 35, 0.5)', borderColor: '#9c7c5e' }}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div 
                  className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                  style={{
                    clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                    backgroundColor: '#d4c19c'
                  }}
                >
                  <Mail className="w-5 h-5 sm:w-7 sm:h-7" style={{ color: '#3a2a23' }} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm mb-1" style={{ color: '#9c7c5e' }}>Email Us Directly</p>
                  <p className="font-bold text-base sm:text-xl group-hover:underline break-all" style={{ color: '#d4c19c' }}>
                    {companyInfo.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="mt-4 sm:mt-6 p-4 sm:p-6 rounded-xl sm:rounded-2xl border" style={{ backgroundColor: 'rgba(58, 42, 35, 0.3)', borderColor: 'rgba(156, 124, 94, 0.3)' }}>
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#d4c19c' }} />
                <p className="text-xs sm:text-sm" style={{ color: '#9c7c5e' }}>Based in</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold" style={{ color: '#d4c19c' }}>Guelph, Ontario</p>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base" style={{ color: '#9c7c5e' }}>Serving the Greater Golden Horseshoe</p>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div id="contact-form" className="rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 border-2" style={{ backgroundColor: 'rgba(58, 42, 35, 0.5)', borderColor: formData.isEmergency ? '#dc2626' : '#9c7c5e' }}>
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-8 sm:py-12">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mb-4 sm:mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                  <CheckCircle className="w-8 h-8 sm:w-12 sm:h-12 text-green-400" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#d4c19c' }}>
                  {formData.isEmergency ? 'Emergency Request Sent!' : 'Message Sent!'}
                </h3>
                <p className="text-sm sm:text-base mb-4" style={{ color: '#9c7c5e' }}>
                  {formData.isEmergency 
                    ? "We've received your emergency request and will contact you ASAP."
                    : "Thank you! We'll get back to you soon."}
                </p>
                <p className="text-xs sm:text-sm" style={{ color: '#9c7c5e' }}>
                  A copy has been sent to your email for reference.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Emergency Badge */}
                {formData.isEmergency && (
                  <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg sm:rounded-xl bg-red-600/20 border border-red-600">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    <span className="text-red-500 font-semibold text-xs sm:text-sm">PRIORITY EMERGENCY REQUEST</span>
                    <button 
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, isEmergency: false }))}
                      className="ml-auto text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <h3 className="text-xl sm:text-2xl font-bold" style={{ color: '#d4c19c' }}>
                  {formData.isEmergency ? 'Emergency Details' : 'Send Us a Message'}
                </h3>
                
                {/* Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="border-2 h-10 sm:h-11 text-sm sm:text-base"
                    style={{ 
                      backgroundColor: 'rgba(42, 31, 26, 0.5)',
                      borderColor: '#5c4a3d',
                      color: '#f5f0e8'
                    }}
                    placeholder="Your name"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-2 h-10 sm:h-11 text-sm sm:text-base"
                      style={{ 
                        backgroundColor: 'rgba(42, 31, 26, 0.5)',
                        borderColor: '#5c4a3d',
                        color: '#f5f0e8'
                      }}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>Phone {formData.isEmergency && '*'}</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required={formData.isEmergency}
                      className="border-2 h-10 sm:h-11 text-sm sm:text-base"
                      style={{ 
                        backgroundColor: 'rgba(42, 31, 26, 0.5)',
                        borderColor: '#5c4a3d',
                        color: '#f5f0e8'
                      }}
                      placeholder="(xxx) xxx-xxxx"
                    />
                  </div>
                </div>

                {/* Budget Range */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>Budget Range</label>
                  <Select value={formData.budget} onValueChange={handleBudgetChange}>
                    <SelectTrigger 
                      className="border-2 h-10 sm:h-11 text-sm sm:text-base"
                      style={{ 
                        backgroundColor: 'rgba(42, 31, 26, 0.5)',
                        borderColor: '#5c4a3d',
                        color: formData.budget ? '#f5f0e8' : '#5c4a3d'
                      }}
                    >
                      <SelectValue placeholder="Select your budget range" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: '#3a2a23', borderColor: '#5c4a3d' }}>
                      {budgetRanges.map((range) => (
                        <SelectItem 
                          key={range.value} 
                          value={range.value}
                          className="text-stone-200 hover:bg-stone-700 focus:bg-stone-700 cursor-pointer text-sm sm:text-base"
                        >
                          {range.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Project Details */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>
                    {formData.isEmergency ? 'Describe the Emergency *' : 'Project Details *'}
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="border-2 resize-none text-sm sm:text-base"
                    style={{ 
                      backgroundColor: 'rgba(42, 31, 26, 0.5)',
                      borderColor: '#5c4a3d',
                      color: '#f5f0e8'
                    }}
                    placeholder={formData.isEmergency 
                      ? "Please describe your electrical emergency..."
                      : "Tell us about your electrical project..."
                    }
                  />
                </div>

                {/* File Uploads */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {/* Project Documents */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>
                      Documents
                    </label>
                    <input
                      ref={projectFileRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleProjectFileUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => projectFileRef.current?.click()}
                      className="w-full p-3 sm:p-4 border-2 border-dashed rounded-lg sm:rounded-xl flex flex-col items-center gap-1 sm:gap-2 transition-all duration-200 hover:border-opacity-100"
                      style={{ borderColor: '#5c4a3d', backgroundColor: 'rgba(42, 31, 26, 0.3)' }}
                    >
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#9c7c5e' }} />
                      <span className="text-xs sm:text-sm" style={{ color: '#9c7c5e' }}>PDFs, Docs</span>
                    </button>
                    {projectFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {projectFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-lg" style={{ backgroundColor: 'rgba(42, 31, 26, 0.5)' }}>
                            <FileText className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: '#d4c19c' }} />
                            <span className="text-[10px] sm:text-xs truncate flex-1" style={{ color: '#c4b8a8' }}>{file.name}</span>
                            <button type="button" onClick={() => removeProjectFile(index)} className="text-red-400 hover:text-red-300 flex-shrink-0">
                              <X className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Images */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2" style={{ color: '#d4c19c' }}>
                      Photos
                    </label>
                    <input
                      ref={imageFileRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => imageFileRef.current?.click()}
                      className="w-full p-3 sm:p-4 border-2 border-dashed rounded-lg sm:rounded-xl flex flex-col items-center gap-1 sm:gap-2 transition-all duration-200 hover:border-opacity-100"
                      style={{ borderColor: '#5c4a3d', backgroundColor: 'rgba(42, 31, 26, 0.3)' }}
                    >
                      <Image className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#9c7c5e' }} />
                      <span className="text-xs sm:text-sm" style={{ color: '#9c7c5e' }}>Images</span>
                    </button>
                    {imageFiles.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2">
                        {imageFiles.map((img, index) => (
                          <div key={index} className="relative w-10 h-10 sm:w-14 sm:h-14 rounded-lg overflow-hidden group">
                            <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                            <button 
                              type="button"
                              onClick={() => removeImage(index)} 
                              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                            >
                              <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/20 border border-red-500">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full font-bold py-5 sm:py-6 text-base sm:text-lg rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ 
                    backgroundColor: formData.isEmergency ? '#dc2626' : '#d4c19c', 
                    color: formData.isEmergency ? '#fff' : '#2a1f1a',
                    boxShadow: formData.isEmergency 
                      ? '0 10px 30px rgba(220, 38, 38, 0.3)'
                      : '0 10px 30px rgba(212, 193, 156, 0.3)'
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : formData.isEmergency ? (
                    <>
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Submit Emergency Request
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Emergency Modal */}
      {showEmergencyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
          <div className="relative max-w-md w-full rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2" style={{ backgroundColor: '#3a2a23', borderColor: '#dc2626' }}>
            <button 
              onClick={() => setShowEmergencyModal(false)}
              className="absolute top-3 sm:top-4 right-3 sm:right-4 text-stone-400 hover:text-stone-200"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
                <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-red-500 mb-3 sm:mb-4">Emergency Electrical Service</h3>
              <p className="text-sm sm:text-base text-stone-300 mb-4 sm:mb-6">
                This will flag your request as a <strong className="text-red-400">PRIORITY EMERGENCY</strong>. 
                We'll do our best to respond as quickly as possible.
              </p>
              <p className="text-xs sm:text-sm text-stone-400 mb-6 sm:mb-8">
                Please only use this for genuine electrical emergencies such as power outages, sparking outlets, burning smells, or safety hazards.
              </p>
              
              <div className="flex gap-3 sm:gap-4">
                <Button
                  onClick={() => setShowEmergencyModal(false)}
                  variant="outline"
                  className="flex-1 py-4 sm:py-5 text-sm sm:text-base"
                  style={{ borderColor: '#5c4a3d', color: '#9c7c5e' }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEmergencySubmit}
                  className="flex-1 py-4 sm:py-5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm sm:text-base"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContactSection;
